import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { auth } from '../firebase';
import { sendPasswordResetEmail } from 'firebase/auth';
import Input from '../components/common/Input';
import Button from '../components/common/Button';

const ForgotPasswordPage = () => {
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage(null);
    setError(null);

    try {
      await sendPasswordResetEmail(auth, email);
      setMessage(
        'Password reset email sent. Check your inbox for further instructions.'
      );
    } catch (err) {
      setError(err.message || 'Failed to send password reset email.');
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-gray-50 to-gray-100 py-12 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
      {/* Back to Home */}
      <div className="absolute top-4 left-4 z-30">
        <Link
          to="/"
          className="flex items-center text-gray-600 hover:text-indigo-600 transition-colors duration-300 group"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-5 w-5 mr-2 transform group-hover:-translate-x-1 transition-transform duration-300"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z"
              clipRule="evenodd"
            />
          </svg>
          <span className="text-sm font-medium">Ana Sayfaya Dön</span>
        </Link>
      </div>

      {/* Decorative Elements */}
      <div className="absolute inset-0 overflow-hidden">
        {/* Music notes animation */}
        <div className="absolute inset-0">
          {[...Array(50)].map((_, i) => (
            <div
              key={i}
              className="absolute text-indigo-600"
              style={{
                top: `${Math.random() * 100}%`,
                left: `${Math.random() * 100}%`,
                fontSize: `${Math.random() * 30 + 20}px`,
                opacity: Math.random() * 0.4 + 0.1,
                transform: `rotate(${Math.random() * 360}deg)`,
                animation: `float ${Math.random() * 15 + 15}s linear ${
                  Math.random() * 5
                }s infinite`,
              }}
            >
              {['♪', '♫', '♬', '♩', '♭', '♮', '♯'][
                Math.floor(Math.random() * 7)
              ]}
            </div>
          ))}
        </div>

        {/* Gradient blobs */}
        <div className="absolute top-10 left-10 w-64 h-64 rounded-full bg-purple-400 filter blur-3xl opacity-10 animate-blob"></div>
        <div className="absolute top-1/3 right-1/4 w-80 h-80 rounded-full bg-indigo-400 filter blur-3xl opacity-10 animate-blob animation-delay-2000"></div>
        <div className="absolute bottom-1/3 left-1/4 w-72 h-72 rounded-full bg-pink-400 filter blur-3xl opacity-10 animate-blob animation-delay-4000"></div>
        <div className="absolute top-1/2 right-5 w-60 h-60 rounded-full bg-indigo-600 filter blur-3xl opacity-10 animate-blob"></div>
        <div className="absolute bottom-10 right-10 w-40 h-40 rounded-full bg-purple-500 filter blur-2xl opacity-10 animate-pulse"></div>
      </div>

      <div className="w-full max-w-md mx-auto z-10 flex-1 flex flex-col justify-center">
        {/* Logo and Title */}
        <div className="text-center mb-10 fade-in">
          <Link to="/" className="inline-block">
            <div className="flex justify-center items-center transition-transform duration-300 hover:scale-105">
              <div className="relative w-16 h-16 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center shadow-lg shadow-indigo-500/20">
                <svg
                  className="h-10 w-10 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3"
                  />
                </svg>
                <div className="absolute -right-2 -top-2 bg-white w-6 h-6 rounded-full flex items-center justify-center border-2 border-indigo-500">
                  <div className="text-xs font-bold text-indigo-700">♪</div>
                </div>
              </div>
              <h1 className="ml-4 text-4xl font-extrabold bg-clip-text text-transparent bg-gradient-to-r from-indigo-600 to-purple-600">
                Harmony Labs Studio
              </h1>
            </div>
          </Link>

          <div className="mt-2 mx-auto w-16 h-1 bg-gradient-to-r from-indigo-400 to-purple-500 rounded-full"></div>
        </div>

        {/* Form Container */}
        <div className="bg-white backdrop-filter backdrop-blur-lg bg-opacity-90 rounded-xl shadow-2xl overflow-hidden border border-gray-200 transform transition-all">
          <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-indigo-500 via-purple-500 to-indigo-500"></div>
          <form onSubmit={handleSubmit} className="p-8 space-y-4">
            {message && (
              <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded relative">
                {message}
              </div>
            )}
            {error && (
              <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative">
                {error}
              </div>
            )}
            <Input
              type="email"
              placeholder="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <Button type="submit" className="w-full">
              Şİfre Yenileme Linki Gönder
            </Button>
          </form>
        </div>

        {/* Sign In Link */}
        <div className="mt-8 text-center slide-up">
          <p className="text-gray-600">
            Hesabınızı hatırladınız mı?{' '}
            <Link
              to="/login"
              className="font-medium text-indigo-600 hover:text-indigo-500 transition-colors hover:underline inline-block"
            >
              Giriş yapın
            </Link>
          </p>
        </div>

        {/* More Information Card */}
        <div className="mt-10 bg-indigo-50 bg-opacity-50 backdrop-filter backdrop-blur-sm rounded-lg p-4 border border-indigo-100 shadow-sm">
          <div className="flex items-start">
            <div className="shrink-0">
              <svg
                className="h-5 w-5 text-indigo-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-indigo-800">
                İYTE Müzik Topluluğu
              </h3>
              <p className="mt-1 text-xs text-indigo-700">
                İYTE Müzik Topluluğu tarafından desteklenen bu platformda stüdyo
                ve müzik aletleri rezervasyonu yapabilirsiniz.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* CSS for Moving Animations */}
      <style jsx>{`
        @keyframes float {
          0% {
            transform: translate(0, 0) rotate(0deg);
            opacity: 0.1;
          }
          10% {
            opacity: 0.4;
          }
          50% {
            transform: translate(-100px, -100px) rotate(180deg);
          }
          90% {
            opacity: 0.4;
          }
          100% {
            transform: translate(0, 0) rotate(360deg);
            opacity: 0.1;
          }
        }

        @keyframes fade-in {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .fade-in {
          animation: fade-in 0.6s ease-out forwards;
        }

        @keyframes slide-up {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .slide-up {
          animation: slide-up 0.6s ease-out forwards;
          animation-delay: 0.3s;
          opacity: 0;
        }

        .animation-delay-2000 {
          animation-delay: 2s;
        }

        .animation-delay-4000 {
          animation-delay: 4s;
        }

        @keyframes blob {
          0% {
            transform: translate(0px, 0px) scale(1);
          }
          33% {
            transform: translate(30px, -50px) scale(1.1);
          }
          66% {
            transform: translate(-20px, 20px) scale(0.9);
          }
          100% {
            transform: translate(0px, 0px) scale(1);
          }
        }

        .animate-blob {
          animation: blob 15s infinite;
        }

        .hover-scale {
          transition: transform 0.3s ease;
        }

        .hover-scale:hover {
          transform: scale(1.05);
        }
      `}</style>
    </div>
  );
};

export default ForgotPasswordPage;